import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { trpc } from "@/app/api/trpcClient";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { adminGuardHOC } from "@/app/hooks/useAdminGuard";

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

function DailyBarChart({ data }: { data: Array<{ date: string; count: number }> }) {
  const maxCount = useMemo(() => Math.max(1, ...data.map((entry) => entry.count)), [data]);

  return (
    <div className="flex items-end gap-3 overflow-x-auto pb-2">
      {data.map((entry) => {
        const barHeight = `${Math.round((entry.count / maxCount) * 100)}%`;
        const parsed = new Date(`${entry.date}T00:00:00`);
        return (
          <div key={entry.date} className="flex min-w-[2.75rem] flex-col items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">{entry.count}</span>
            <div
              className="w-8 rounded-md bg-blue-500/80 shadow-sm"
              style={{ height: barHeight, minHeight: "4px" }}
              aria-hidden
            />
            <span className="text-[0.65rem] text-slate-500">{dateFormatter.format(parsed)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ScopeSummary({
  title,
  data,
}: {
  title: string;
  data: Array<{ target: string; count: number }>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>Top interactions in the selected window</CardDescription>
      </CardHeader>
      <CardContent className="pb-6">
        {data.length === 0 ? (
          <p className="text-sm text-slate-500">No activity yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.map((item) => (
              <li
                key={`${title}-${item.target}`}
                className="flex items-center justify-between rounded-lg border border-slate-200/60 px-3 py-2 text-sm"
              >
                <span className="truncate pr-3 font-medium text-slate-700" title={item.target}>
                  {item.target}
                </span>
                <span className="font-semibold text-slate-900">{item.count}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RecentEventsTable({
  events,
}: {
  events: Array<{
    id: string;
    userId: string;
    userEmail: string | null;
    scope: "page" | "component" | "button";
    target: string;
    action: string;
    metadata: Record<string, unknown> | null;
    occurredAt: Date;
  }>;
}) {
  const formatMetadata = (metadata: Record<string, unknown> | null) => {
    if (!metadata || Object.keys(metadata).length === 0) return "—";
    try {
      const serialized = JSON.stringify(metadata);
      return serialized.length > 80 ? `${serialized.slice(0, 77)}…` : serialized;
    } catch {
      return "[unserializable]";
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              When
            </th>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              User
            </th>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              Scope
            </th>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              Target
            </th>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              Action
            </th>
            <th scope="col" className="px-4 py-3 text-left font-semibold text-slate-600">
              Metadata
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {events.map((event) => (
            <tr key={event.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 text-slate-700">{event.occurredAt.toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-700">
                <div className="flex flex-col">
                  <span className="font-medium">{event.userEmail ?? event.userId}</span>
                  {event.userEmail && event.userEmail !== event.userId && (
                    <span className="text-xs text-slate-500">{event.userId}</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 capitalize text-slate-700">{event.scope}</td>
              <td className="px-4 py-3 font-medium text-slate-800" title={event.target}>
                {event.target}
              </td>
              <td className="px-4 py-3 capitalize text-slate-700">{event.action}</td>
              <td className="px-4 py-3 text-slate-600" title={formatMetadata(event.metadata)}>
                {formatMetadata(event.metadata)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserViewsByDayChart() {
  const { data, isLoading, isError } = trpc.accessMetrics.getUserViewsByDay.useQuery({
    days: 14,
  });

  const chartData = useMemo(() => {
    if (!data) return [];

    // Transform data for Recharts - each date becomes a row with user counts as columns
    return data.dates.map((date) => {
      const row: Record<string, string | number> = { date };
      for (const series of data.series) {
        const dayData = series.data.find((d) => d.date === date);
        row[series.userEmail] = dayData?.count ?? 0;
      }
      return row;
    });
  }, [data]);

  const colors = [
    "#3b82f6", // blue-500
    "#ef4444", // red-500
    "#10b981", // green-500
    "#f59e0b", // amber-500
    "#8b5cf6", // violet-500
    "#ec4899", // pink-500
    "#06b6d4", // cyan-500
    "#f97316", // orange-500
    "#14b8a6", // teal-500
    "#a855f7", // purple-500
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User views by day</CardTitle>
          <CardDescription>View count per user over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-80 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data || data.series.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">User views by day</CardTitle>
          <CardDescription>View count per user over time</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500">No user activity data available yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">User views by day</CardTitle>
        <CardDescription>
          View count per user over time. Showing top {data.series.length} most active users.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => dateFormatter.format(new Date(`${date}T00:00:00`))}
              stroke="#64748b"
              style={{ fontSize: "12px" }}
            />
            <YAxis stroke="#64748b" style={{ fontSize: "12px" }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
              labelFormatter={(date) => new Date(`${date}T00:00:00`).toLocaleDateString()}
            />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            {data.series.slice(0, 10).map((series, index) => (
              <Line
                key={series.userId}
                type="monotone"
                dataKey={series.userEmail}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function AccessMetricsPage() {
  const { data, isLoading, isError, refetch, isRefetching } =
    trpc.accessMetrics.getSummary.useQuery({
      days: 14,
      limit: 100,
    });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-600">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          <span className="text-sm font-medium">Loading access metrics…</span>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h2 className="text-lg font-semibold text-red-700">Unable to load access metrics</h2>
          <p className="mt-2 text-sm text-red-600">
            Something went wrong while fetching the analytics data. Please try again.
          </p>
          <button
            className="mt-4 inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Usage Analytics</h1>
          <p className="text-sm text-slate-600">
            Track how teammates interact with pages, components, and key actions over the past two
            weeks.
          </p>
        </div>
        <button
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          onClick={() => void refetch()}
          disabled={isRefetching}
        >
          {isRefetching ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
          ) : (
            <span role="img" aria-hidden>
              🔄
            </span>
          )}
          Refresh
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Daily activity</CardTitle>
          <CardDescription>
            Unique events recorded per day. Use this to spot peaks in engagement.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DailyBarChart data={data.dailyCounts} />
        </CardContent>
      </Card>

      <UserViewsByDayChart />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <ScopeSummary title="Page views" data={data.countsByScope.page} />
        <ScopeSummary title="Component opens" data={data.countsByScope.component} />
        <ScopeSummary title="Button clicks" data={data.countsByScope.button} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Latest interactions</CardTitle>
          <CardDescription>
            The most recent {data.recentEvents.length} events captured within the selected window.
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          <RecentEventsTable events={data.recentEvents} />
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/admin/analytics")({
  component: adminGuardHOC(AccessMetricsPage),
});
